/**
 * Q&A — AppSync transport.
 *
 * Direct AppSync talk via Amplify. All operations target the
 * slideHandler Lambda data source through fieldName dispatch:
 *   - getQuestionsByWebinar (query)
 *   - askQuestion (mutation → onQuestionAsked)
 *   - upvoteQuestion, answerQuestion, dismissQuestion, deleteQuestion,
 *     publishQuestion (mutations → onQuestionUpdated)
 *
 * Inline GraphQL documents so this file compiles without codegen.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { configureAppSync } from "../appsync/config";

export interface QnaAnswer {
  id: string;
  responderId: string;
  responderName: string;
  responderEmail: string | null;
  responderRole: string;
  body: string;
  isLiveAnswer: boolean;
  createdAt: string;
}

export interface QnaQuestion {
  id: string;
  webinarId: string;
  askerId: string | null;
  askerName: string;
  askerRole: string;
  body: string;
  isAnonymous: boolean;
  status: "OPEN" | "ANSWERED" | "DISMISSED" | "DELETED";
  isPinned: boolean;
  isHighlighted: boolean;
  isPublic: boolean;
  upvoteCount: number;
  answers: QnaAnswer[];
  answeredBy: string | null;
  answeredByName: string | null;
  answerType: "LIVE" | "TYPED" | null;
  createdAt: string;
}

export interface UseQnaAppSyncParams {
  webinarId: string;
  userId: string;
  userName: string;
  userEmail?: string;
  role: "HOST" | "SPEAKER" | "ATTENDEE";
}

export interface UseQnaAppSyncResult {
  questions: QnaQuestion[];
  isConnected: boolean;
  error: string | null;
  askQuestion: (body: string, isAnonymous?: boolean) => Promise<void>;
  upvoteQuestion: (questionId: string) => Promise<void>;
  answerLive: (questionId: string) => Promise<void>;
  answerText: (questionId: string, body: string) => Promise<void>;
  dismissQuestion: (questionId: string) => Promise<void>;
  deleteQuestion: (questionId: string) => Promise<void>;
  publishQuestion: (questionId: string) => Promise<void>;
  clearError: () => void;
}

// ---------- Inline GraphQL documents ----------

const QUESTION_FIELDS = `
  id
  webinarId
  askerId
  askerName
  askerRole
  body
  isAnonymous
  status
  isPinned
  isHighlighted
  isPublic
  upvoteCount
  answers {
    id
    responderId
    responderName
    responderEmail
    responderRole
    body
    isLiveAnswer
    createdAt
  }
  answeredBy
  answeredByName
  answerType
  createdAt
`;

const GET_QUESTIONS = /* GraphQL */ `
  query GetQuestionsByWebinar(
    $webinarId: String!
    $userId: String
    $userRole: String
  ) {
    getQuestionsByWebinar(
      webinarId: $webinarId
      userId: $userId
      userRole: $userRole
    ) {
      ${QUESTION_FIELDS}
    }
  }
`;

const ASK_QUESTION = /* GraphQL */ `
  mutation AskQuestion($input: AskQuestionInput!) {
    askQuestion(input: $input) {
      ${QUESTION_FIELDS}
    }
  }
`;

const UPVOTE_QUESTION = /* GraphQL */ `
  mutation UpvoteQuestion($input: QuestionActionInput!) {
    upvoteQuestion(input: $input) {
      ${QUESTION_FIELDS}
    }
  }
`;

const ANSWER_QUESTION = /* GraphQL */ `
  mutation AnswerQuestion($input: AnswerQuestionInput!) {
    answerQuestion(input: $input) {
      ${QUESTION_FIELDS}
    }
  }
`;

const DISMISS_QUESTION = /* GraphQL */ `
  mutation DismissQuestion($input: QuestionActionInput!) {
    dismissQuestion(input: $input) {
      ${QUESTION_FIELDS}
    }
  }
`;

const DELETE_QUESTION = /* GraphQL */ `
  mutation DeleteQuestion($input: QuestionActionInput!) {
    deleteQuestion(input: $input) {
      ${QUESTION_FIELDS}
    }
  }
`;

const PUBLISH_QUESTION = /* GraphQL */ `
  mutation PublishQuestion($input: QuestionActionInput!) {
    publishQuestion(input: $input) {
      ${QUESTION_FIELDS}
    }
  }
`;

const ON_QUESTION_ASKED = /* GraphQL */ `
  subscription OnQuestionAsked($webinarId: String!) {
    onQuestionAsked(webinarId: $webinarId) {
      ${QUESTION_FIELDS}
    }
  }
`;

const ON_QUESTION_UPDATED = /* GraphQL */ `
  subscription OnQuestionUpdated($webinarId: String!) {
    onQuestionUpdated(webinarId: $webinarId) {
      ${QUESTION_FIELDS}
    }
  }
`;

export function useQnaAppSync(params: UseQnaAppSyncParams): UseQnaAppSyncResult {
  const { webinarId, userId, userName, userEmail, role } = params;
  const [questions, setQuestions] = useState<QnaQuestion[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    configureAppSync();
  }, []);

  const client = useMemo(() => generateClient(), []);

  const upsert = useCallback((q: QnaQuestion | null | undefined) => {
    if (!q || !q.id) return;
    setQuestions((prev) => {
      const idx = prev.findIndex((x) => x.id === q.id);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = q;
        return next;
      }
      const next = [...prev, q];
      next.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      return next;
    });
  }, []);

  // Role-aware visibility — mirrors the server filter in
  // handleGetQuestionsByWebinar. Kept here so subscription events
  // (which AppSync broadcasts to every subscriber) don't leak other
  // attendees' private questions into this client.
  const isVisibleToMe = useCallback(
    (q: QnaQuestion) => {
      if (role !== "ATTENDEE") return true;
      return q.askerId === userId || !!q.isPublic;
    },
    [role, userId],
  );

  // History
  useEffect(() => {
    if (!webinarId) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await client.graphql({
          query: GET_QUESTIONS,
          variables: { webinarId, userId, userRole: role },
        });
        if (cancelled) return;
        const items: QnaQuestion[] = res?.data?.getQuestionsByWebinar || [];
        items.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        setQuestions(items);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Failed to load questions");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [webinarId, client, userId, role]);

  // Subscriptions — onQuestionAsked + onQuestionUpdated
  useEffect(() => {
    if (!webinarId) return;
    const subs: Array<{ unsubscribe: () => void }> = [];
    // Track error count across both subscriptions — only flip
    // isConnected=false after BOTH fail, so a transient error on one
    // doesn't flicker the "live" badge.
    let errorCount = 0;

    const open = (query: string, eventKey: "onQuestionAsked" | "onQuestionUpdated") => {
      try {
        const observable = client.graphql({
          query,
          variables: { webinarId },
        }) as any;
        const s = observable.subscribe({
          next: ({ data }: any) => {
            setIsConnected(true);
            setError(null);
            errorCount = 0;
            const payload = data?.[eventKey] as QnaQuestion | undefined;
            if (!payload) return;
            // AppSync broadcasts subscription events to every subscriber —
            // drop anything this role/user shouldn't see.
            if (!isVisibleToMe(payload)) return;
            upsert(payload);
          },
          error: (err: any) => {
            errorCount += 1;
            console.error(`[useQnaAppSync] ${eventKey} error`, err);
            const msg =
              err?.errors?.[0]?.message ||
              err?.message ||
              (typeof err === 'string' ? err : 'Subscription error');
            setError(msg);
            if (errorCount >= 2) {
              // Both subscriptions failed — mark as disconnected.
              setIsConnected(false);
            }
          },
        });
        subs.push(s);
      } catch (err: any) {
        setError(err?.message || "Failed to open subscription");
      }
    };

    open(ON_QUESTION_ASKED, "onQuestionAsked");
    open(ON_QUESTION_UPDATED, "onQuestionUpdated");
    // Optimistically show "live" — if either subscription eventually
    // errors, the error callback above will flip this back.
    setIsConnected(true);

    return () => {
      for (const s of subs) {
        try {
          s.unsubscribe();
        } catch {
          /* ignore */
        }
      }
      setIsConnected(false);
    };
  }, [webinarId, client, upsert, isVisibleToMe]);

  // Small helper that runs a mutation and upserts the returned question
  const runMutation = useCallback(
    async (query: string, fieldName: string, input: any, label?: string): Promise<void> => {
      try {
        const res: any = await client.graphql({
          query,
          variables: { input },
        });
        const q: QnaQuestion | undefined = res?.data?.[fieldName];
        if (q) upsert(q);
      } catch (err: any) {
        console.error(`[runMutation] ${fieldName} failed`, err);
        const finalError = label || err?.message || "Operation failed";
        setError(finalError);
      }
    },
    [client, upsert],
  );

  const askQuestion = useCallback(
    async (body: string, isAnonymous?: boolean) => {
      const text = (body || "").trim();
      if (!text) return;
      await runMutation(ASK_QUESTION, "askQuestion", {
        webinarId,
        askerId: userId,
        askerName: userName,
        askerEmail: userEmail ?? null,
        askerRole: role,
        body: text,
        isAnonymous: !!isAnonymous,
      }, "Failed to send question");
    },
    [runMutation, webinarId, userId, userName, userEmail, role],
  );

  const actionInput = useCallback(
    (questionId: string) => ({
      webinarId,
      questionId,
      userId,
      userName,
      userRole: role,
    }),
    [webinarId, userId, userName, role],
  );

  const upvoteQuestion = useCallback(
    async (questionId: string) => {
      await runMutation(UPVOTE_QUESTION, "upvoteQuestion", actionInput(questionId), "Failed to upvote");
    },
    [runMutation, actionInput],
  );

  const answerLive = useCallback(
    async (questionId: string) => {
      await runMutation(ANSWER_QUESTION, "answerQuestion", {
        webinarId,
        questionId,
        type: "LIVE",
        body: null,
        responderId: userId,
        responderName: userName,
        responderEmail: userEmail ?? null,
        responderRole: role,
      }, "Failed to mark as live answer");
    },
    [runMutation, webinarId, userId, userName, userEmail, role],
  );

  const answerText = useCallback(
    async (questionId: string, body: string) => {
      const trimmed = (body || "").trim();
      if (!trimmed) return;
      await runMutation(ANSWER_QUESTION, "answerQuestion", {
        webinarId,
        questionId,
        type: "TYPED",
        body: trimmed,
        responderId: userId,
        responderName: userName,
        responderEmail: userEmail ?? null,
        responderRole: role,
      }, "Failed to send answer");
    },
    [runMutation, webinarId, userId, userName, userEmail, role],
  );

  const dismissQuestion = useCallback(
    async (questionId: string) => {
      await runMutation(DISMISS_QUESTION, "dismissQuestion", actionInput(questionId), "Failed to dismiss question");
    },
    [runMutation, actionInput],
  );

  const deleteQuestion = useCallback(
    async (questionId: string) => {
      await runMutation(DELETE_QUESTION, "deleteQuestion", actionInput(questionId), "Failed to delete question");
    },
    [runMutation, actionInput],
  );

  const publishQuestion = useCallback(
    async (questionId: string) => {
      await runMutation(PUBLISH_QUESTION, "publishQuestion", actionInput(questionId), "Failed to publish question");
    },
    [runMutation, actionInput],
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    questions,
    isConnected,
    error,
    askQuestion,
    upvoteQuestion,
    answerLive,
    answerText,
    dismissQuestion,
    deleteQuestion,
    publishQuestion,
    clearError,
  };
}
